
import { EventModel } from "@bryntum/schedulerpro";
import { ArrowDownTrayIcon } from "@heroicons/vue/20/solid";
import { InformationCircleIcon } from "@heroicons/vue/24/solid";
import { processClasses, durationService } from "@oculai/planner";
import { format } from "date-fns";
import { defineComponent, PropType } from "vue";
import ImagesComparison from "@/components/UI/ImagesComparison.vue";
import LoadingSection from "@/components/UI/LoadingSection.vue";
import PlannerComments from "@/components/planner/PlannerComments.vue";
import SlideOver from "@/components/planner/SlideOver.vue";
import CameraRepository from "@/repositories/CameraRepository";
import { downloadZip, createZipFromUrls } from "@/services/zip";
import { ProjectDurationSettings } from "@/types/ProjectDurationSettings";

export const encodedLabelsToDecodedLabel = processClasses.reduce((acc, processClass) => {
  acc[processClass.encodedLabel] = processClass.decodedLabel;

  return acc;
}, {} as Record<string, string>);

export default defineComponent({
  name: "ActualSideBar",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    cameraList: {
      type: Array as PropType<Array<string>>,
      required: true,
    },
    resourceFullName: {
      type: String,
      required: true,
    },
    events: {
      type: Array as PropType<Array<EventModel>>,
    },
    sourceId: {
      type: String as PropType<string>,
      required: true,
    },
    projectDurationSettings: {
      type: Object as PropType<ProjectDurationSettings>,
      required: false,
    },
  },
  emits: ["closed", "commentAdded", "commentDeleted"],
  components: {
    PlannerComments,
    LoadingSection,
    ImagesComparison,
    SlideOver,
    ArrowDownTrayIcon,
  },
  data() {
    return {
      videoUrls: {} as Record<string, string>,
      videoUrlLoading: false as boolean,
      selectedProcessIndex: 0 as number,
      processTabSkip: 0 as number,
      hasDailyReportPermission: false as boolean,
      loading: false as boolean,
      actualEvent: {} as EventModel,
    };
  },

  watch: {
    events(value) {
      if (value) {
        this.actualEvent = this.events?.find(
          (item) => item.getData("type") === "actual",
        ) as EventModel;
      }
    },
  },

  computed: {
    initialBeforeDate() {
      return this.actualEvent ? format(this.actualEvent.getData("startDate"), "yyyy-MM-dd") : "";
    },
    initialAfterDate() {
      return this.actualEvent ? format(this.actualEvent.getData("endDate"), "yyyy-MM-dd") : "";
    },
    sortedEvents() {
      if (this.events?.length === 1) {
        return this.events;
      }
      let temp = [] as EventModel[];
      if (this.events && this.events?.length > 1) {
        temp = [this.events[1], this.events[0]];
      }

      return temp;
    },
    plannerCommentsKey() {
      return `${this.currentCustomerName}-${this.currentSiteId}-${this.sourceId},
      )}`;
    },
    durationSettings() {
      return this.projectDurationSettings
        ? durationService.calculateSettings(
            this.projectDurationSettings.working_hours,
            this.projectDurationSettings.non_working_days,
          )
        : null;
    },
  },

  methods: {
    InformationCircleIcon,
    format,
    async downloadImages(camId: string) {
      const { customer_name, site_id } = this.$route.params;
      const imageList = await CameraRepository.getCameraGalleryForTimeRange(
        customer_name as string,
        site_id as string,
        camId,
        this.initialBeforeDate,
        this.initialAfterDate,
      );
      this.loading = true;
      createZipFromUrls(imageList)
        .then((zip) => {
          downloadZip(zip, this.resourceFullName, this.initialBeforeDate, this.initialAfterDate);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getWorkingDays(event: EventModel) {
      if (!this.durationSettings) {
        return 0;
      }
      return durationService.calculateDuration(
        this.durationSettings,
        event.startDate as Date,
        event.endDate as Date,
      ).workingDays;
    },
    formatNumber(value: number) {
      return value.toLocaleString(this.$i18n.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      });
    },
  },
});
